/**
 * Cart Page Redirect with Drawer Opening (Horizon theme)
 * - Redirects /cart visitors to a collection page and opens the cart drawer
 * - Detects checkout referrer and opens cart drawer on homepage
 * - Optionally removes a specific variant when returning from checkout
 *
 * Requires theme.liquid to load this file with type="module" so
 * import('@theme/cart-store') resolves via the theme importmap.
 */
(function () {
  'use strict';

  const CART_REDIRECT_PATH = '/collections/shop-all-products';
  const OPEN_CART_PARAM = 'open_cart';
  const TARGET_VARIANT_ID = '46184858157206';

  /**
   * Check if a referrer string indicates checkout
   * @param {string} referrer - The referrer URL string
   * @returns {boolean}
   */
  function checkIfCheckoutReferrer(referrer) {
    if (!referrer) return false;
    try {
      const referrerUrl = new URL(referrer);
      const referrerHost = referrerUrl.hostname.toLowerCase();
      const referrerPath = referrerUrl.pathname.toLowerCase();
      const isCheckoutHost =
        referrerHost.includes('checkout') ||
        referrerHost === 'checkout.shopify.com' ||
        referrerHost.includes('checkout.shopifycs.com');
      const isCheckoutPath = referrerPath.includes('/checkout');
      return isCheckoutHost || isCheckoutPath;
    } catch (e) {
      return referrer.toLowerCase().includes('checkout');
    }
  }

  /**
   * Check if the user came from Shopify checkout (referrer + sessionStorage backup)
   * @returns {boolean}
   */
  function isFromCheckout() {
    try {
      const fromCheckout = sessionStorage.getItem('from_checkout');
      if (fromCheckout === 'true') {
        sessionStorage.removeItem('from_checkout');
        return true;
      }
    } catch (e) {}
    const isCheckout = checkIfCheckoutReferrer(document.referrer);
    if (isCheckout) {
      try {
        sessionStorage.setItem('from_checkout', 'true');
      } catch (e) {}
    }
    return isCheckout;
  }

  // Store referrer early so it isn’t lost
  if (document.referrer && checkIfCheckoutReferrer(document.referrer)) {
    try {
      sessionStorage.setItem('from_checkout', 'true');
    } catch (e) {}
  }

  /**
   * Open the theme’s cart drawer (cart-drawer-component)
   * @returns {boolean}
   */
  function openCartDrawer() {
    const cartDrawer = document.querySelector('cart-drawer-component');
    if (cartDrawer && typeof cartDrawer.open === 'function') {
      try {
        cartDrawer.open();
        return true;
      } catch (e) {}
    }
    const trigger = document.querySelector('[data-testid="cart-drawer-trigger"]');
    if (trigger) {
      try {
        trigger.click();
        return true;
      } catch (e) {}
    }
    return false;
  }

  /**
   * Wait for cart-drawer-component to be defined and upgraded
   * @returns {Promise<void>}
   */
  function waitForCartDrawerComponent() {
    return new Promise((resolve) => {
      if (customElements.get('cart-drawer-component')) {
        setTimeout(resolve, 100);
        return;
      }
      const checkInterval = setInterval(() => {
        if (customElements.get('cart-drawer-component')) {
          clearInterval(checkInterval);
          setTimeout(resolve, 200);
        }
      }, 100);
      setTimeout(() => {
        clearInterval(checkInterval);
        resolve();
      }, 3000);
    });
  }

  /**
   * Try to open the cart drawer with retries
   */
  async function tryOpenCartDrawer() {
    await waitForCartDrawerComponent();
    let attempts = 0;
    const maxAttempts = 10;
    const tryOpen = () => {
      attempts++;
      if (openCartDrawer() || attempts >= maxAttempts) return;
      const delay = Math.min(200 + attempts * 100, 500);
      setTimeout(tryOpen, delay);
    };
    tryOpen();
  }

  /**
   * Fetch current cart from /cart.js
   * @returns {Promise<{ items: Array }>}
   */
  async function getCurrentCart() {
    try {
      const response = await fetch('/cart.js');
      if (!response.ok) throw new Error(`Cart fetch: ${response.status}`);
      return await response.json();
    } catch (error) {
      return { items: [] };
    }
  }

  /**
   * Refresh cart UI via theme cart store
   */
  async function refreshCartUI() {
    try {
      const CartModule = await import('@theme/cart-store');
      const cartStore = CartModule.default || CartModule.Cart;
      if (cartStore?.refresh) {
        cartStore._isInternalRefresh = true;
        await cartStore.refresh();
      }
    } catch (error) {
      // Cart store may not be loaded; non-fatal
    }
  }

  /**
   * Remove a line item by variant ID
   * @param {string|number} variantId
   * @returns {Promise<boolean>}
   */
  async function removeVariantFromCart(variantId) {
    try {
      const cart = await getCurrentCart();
      const item = cart.items.find((i) => String(i.variant_id) === String(variantId));
      if (!item) return false;
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: item.key, quantity: 0 }),
      });
      if (!response.ok) return false;
      await refreshCartUI();
      return true;
    } catch (error) {
      return false;
    }
  }

  const isCartPage = window.location.pathname === '/cart' || window.location.pathname === '/cart/';

  if (isCartPage) {
    if (checkIfCheckoutReferrer(document.referrer)) {
      try {
        sessionStorage.setItem('from_checkout', 'true');
      } catch (e) {}
    }
    const redirectUrl = `${window.location.origin}${CART_REDIRECT_PATH}?${OPEN_CART_PARAM}=true`;
    window.location.replace(redirectUrl);
    return;
  }

  const urlParams = new URLSearchParams(window.location.search);
  const shouldOpenCartFromUrl = urlParams.get(OPEN_CART_PARAM) === 'true';
  const isHomepage = window.location.pathname === '/' || window.location.pathname === '';
  const cameFromCheckout = isFromCheckout();
  const shouldOpenCartFromCheckout = isHomepage && cameFromCheckout;

  if (cameFromCheckout) {
    removeVariantFromCart(TARGET_VARIANT_ID);
  }

  if (shouldOpenCartFromUrl || shouldOpenCartFromCheckout) {
    if (shouldOpenCartFromUrl) {
      const cleanUrl = window.location.origin + window.location.pathname;
      window.history.replaceState({}, document.title, cleanUrl);
    }

    function initCartDrawer() {
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => {
          window.addEventListener('load', () => {
            setTimeout(() => tryOpenCartDrawer(), 300);
          });
        });
      } else if (document.readyState === 'interactive') {
        window.addEventListener('load', () => {
          setTimeout(() => tryOpenCartDrawer(), 300);
        });
      } else {
        setTimeout(() => tryOpenCartDrawer(), 300);
      }
    }
    initCartDrawer();
  }
})();
